import React, { useState, useEffect, useRef } from 'react';
import BRAND from '../brand';
import { useLegalLinks } from '../legal';
import { useDeliverySlots } from '../deliverySlots';
import { fetchAppSettings } from '../settings';
import { api, getTelegramUser, fetchWebUser } from '../api';
import { openLink as platformOpenLink, openPlatformLink, getDevicePlatform, getPlatform } from '../platform';
import { payViaTgInvoice } from '../payments';
import AddressBlock from './AddressBlock';
import TimeSlotPicker from './TimeSlotPicker';
import { FieldIcon, HeroIcon, SectionLoader } from './NavIcons';
import SmsAuthInline from './SmsAuthInline';
import { todayShop, useShopTimezone } from '../utils/timezone';
import {
  trackSubscriptionStarted,
  trackSubscriptionAddressInZone,
  trackSubscriptionAddressOutOfZone,
  trackClickTelegram,
  trackClickMax,
} from '../utils/metrika';
import { getTouchToken, getWebClientId, maxBotUrl, tgBotUrl } from '../utils/attribution';


// Окно дат подписки считается в поясе магазина из настроек (подстановок нет): форму
// показываем, когда он известен — даты нужны уже в начальном состоянии календаря.
function SubscriptionCheckout(props) {
  const shopTz = useShopTimezone();
  if (!shopTz) return <SectionLoader />;
  return <SubscriptionCheckoutForm {...props} shopTz={shopTz} />;
}

function SubscriptionCheckoutForm({ subscriptionData, onBack, onSuccess, shopTz }) {
  const legal = useLegalLinks();
  const slotsConfig = useDeliverySlots();
  const platform = getPlatform();
  const [webUser, setWebUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  const telegramUser = getTelegramUser();
  const isWebGuest = platform === 'web' && !localStorage.getItem('stems_jwt');
  const isWebAuth = platform === 'web' && !!localStorage.getItem('stems_jwt');
  // В подписках нужен пользователь обязательно — если web и есть JWT, берём webUser, иначе telegramUser
  const user = webUser || telegramUser || {};

  // Подгружаем web-юзера при монтировании если уже авторизован
  useEffect(() => {
    if (isWebAuth && !webUser) {
      fetchWebUser().then(u => { if (u) setWebUser(u); }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { catalog_item, delivery_count, discount_percent, subtotal, discount_amount, service_fee, total, use_bonuses: initialUseBonuses, bonus_discount: initialBonusDiscount, odd_quantity: initialOddQuantity } = subscriptionData;

  const [showVaseTooltip, setShowVaseTooltip] = useState(false);
  const [vaseMinDeliveries, setVaseMinDeliveries] = useState(4);
  const [subDeliveryMode, setSubDeliveryMode] = useState('free');

  useEffect(() => {
    fetchAppSettings().then(s => {
      setVaseMinDeliveries(s.vase_min_deliveries || 4);
      setSubDeliveryMode(s.subscription_delivery_mode || 'free');
    }).catch(() => {});
  }, []);

  // Навигацию снизу оставляем видимой — пользователь может вернуться в меню/профиль

  const dismissVaseTooltip = () => { if (showVaseTooltip) setShowVaseTooltip(false); };

  // Даты
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const calendarRef = useRef(null);
  const timePickerRef = useRef(null);

  // Окно дат подписки — в поясе магазина
  const today = todayShop(shopTz);
  const minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const maxDate = new Date(today.getFullYear(), today.getMonth() + 2, today.getDate());

  // Сохранение/загрузка формы
  const SUB_STORAGE_KEY = 'stems_sub_checkout_form';
  const getSavedForm = () => {
    try {
      const saved = localStorage.getItem(SUB_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return null;
  };
  const saved = getSavedForm();

  // Тип клиента
  const [clientType, setClientType] = useState(saved?.clientType || 'individual');
  const [companyFile, setCompanyFile] = useState(null);
  const [companySendLater, setCompanySendLater] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Адрес
  const [formData, setFormData] = useState({
    delivery_address: saved?.delivery_address || '',
    delivery_apartment: saved?.delivery_apartment || '',
    delivery_entrance: saved?.delivery_entrance || '',
    delivery_floor: saved?.delivery_floor || '',
    delivery_intercom: saved?.delivery_intercom || '',
    delivery_apartment_absent: saved?.delivery_apartment_absent || false,
  });
  const [addressCoordinates, setAddressCoordinates] = useState(saved?.address_coordinates || null);
  const [addressWarning, setAddressWarning] = useState(false);
  const [deliveryCalc, setDeliveryCalc] = useState(null);

  // Контакт
  // Приоритет для имени: TG → Max → user.first_name (заблокированный после первого заказа web-юзера)
  const _initialSubName = saved?.name
    || user?.tg_first_name
    || user?.max_first_name
    || user?.first_name
    || '';
  const [name, setName] = useState(_initialSubName);
  const [phone, setPhone] = useState(() => {
    const raw = saved?.phone || '+7';
    const d = raw.replace(/\D/g, '');
    return d.length >= 1 ? d : '7';
  });
  const [email, setEmail] = useState(saved?.email || '');

  // Получатель (только физлицо)
  const [isRecipientOther, setIsRecipientOther] = useState(saved?.isRecipientOther || false);
  const [recipientName, setRecipientName] = useState(saved?.recipientName || '');
  const [recipientPhone, setRecipientPhone] = useState(() => {
    const raw = saved?.recipientPhone || '+7';
    const d = raw.replace(/\D/g, '');
    return d.length >= 1 ? d : '7';
  });
  // Согласия 152-ФЗ — НЕ сохраняем в localStorage, всегда требуем заново
  const [consentPd, setConsentPd] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);

  // При первой загрузке webUser перезаписываем имя/телефон значениями из профиля.
  // Доверяем данным из аккаунта больше чем сохранённой форме (где могло остаться «Гость»).
  // Только один раз — потом юзер может править поля свободно.
  const subPrefilledRef = useRef(false);
  useEffect(() => {
    if (!webUser || subPrefilledRef.current) return;
    if (webUser.phone) {
      let d = String(webUser.phone).replace(/\D/g, '');
      if (d.startsWith('8')) d = '7' + d.substring(1);
      if (d.length === 10) d = '7' + d;
      setPhone(d.substring(0, 11));
    }
    const candidate = webUser.tg_first_name || webUser.max_first_name || webUser.first_name || '';
    if (candidate) setName(candidate);
    subPrefilledRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webUser]);

  // Сохраняем форму при изменениях
  useEffect(() => {
    try {
      localStorage.setItem(SUB_STORAGE_KEY, JSON.stringify({
        clientType, name, phone, email,
        delivery_address: formData.delivery_address,
        delivery_apartment: formData.delivery_apartment,
        delivery_entrance: formData.delivery_entrance,
        delivery_floor: formData.delivery_floor,
        delivery_intercom: formData.delivery_intercom,
        delivery_apartment_absent: formData.delivery_apartment_absent,
        isRecipientOther, recipientName, recipientPhone,
        address_coordinates: addressCoordinates,
      }));
    } catch (e) {}
  }, [clientType, name, phone, email, formData, isRecipientOther, recipientName, recipientPhone, addressCoordinates]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Idempotency-key — UUID, сгенерированный 1 раз на форму. Защищает от дублей при ретраях
  // (если бэк ответил с ошибкой/таймаутом, повторный submit с тем же ключом вернёт ту же подписку).
  const [idempotencyKey] = useState(() => {
    try { return (crypto?.randomUUID?.() || `sub-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`); }
    catch { return `sub-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; }
  });


  // Закрытие по клику мимо
  const datePickerRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (datePickerRef.current && datePickerRef.current.contains(e.target)) return;
      if (timePickerRef.current && timePickerRef.current.contains(e.target)) return;
      if (showCalendar && calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
      if (showTimePicker && timePickerRef.current && !timePickerRef.current.contains(e.target)) {
        setShowTimePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showCalendar, showTimePicker]);

  // ===== Календарь =====
  const toDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const isDateBlocked = (date) => {
    if (date < minDate) return true;
    if (date > maxDate) return true;
    return false;
  };

  const toggleDate = (date) => {
    const dateStr = toDateStr(date);
    setSelectedDates(prev => {
      if (prev.includes(dateStr)) {
        return prev.filter(d => d !== dateStr);
      }
      if (prev.length >= delivery_count) return prev;
      return [...prev, dateStr].sort();
    });
  };

  // Автозакрытие календаря при полном выборе дат
  useEffect(() => {
    if (selectedDates.length === delivery_count && showCalendar) {
      setShowCalendar(false);
    }
    // showCalendar НЕ в deps — избегаем цикла (effect сам его меняет)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates, delivery_count]);

  // ===== Адрес =====
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (address, data) => {
    updateField('delivery_address', address);
    if (data && data.geo_lat && data.geo_lon) {
      // Выбрано из подсказок — координаты достоверные
      setAddressCoordinates({ lat: data.geo_lat, lon: data.geo_lon });
      setAddressWarning(false);
    } else {
      // Ручной ввод — сбрасываем координаты, адрес нужно выбрать из подсказок
      setAddressCoordinates(null);
      setAddressWarning(false);
    }
  };

  const handleAddressBlur = async (currentValue) => {
    const addr = currentValue || formData.delivery_address;
    if (!addr || addr.length < 5) return;
    if (addressCoordinates) return; // уже выбран из подсказок
    // Пробуем найти точный адрес через Dadata
    try {
      const result = await api.request(`/address/suggest?query=${encodeURIComponent(addr)}&count=1`);
      const suggestion = result.suggestions?.[0];
      // Принимаем только если Dadata нашла реальный дом (есть postal_code и координаты)
      if (suggestion?.data?.house && suggestion?.data?.geo_lat && suggestion?.data?.geo_lon && suggestion?.data?.postal_code) {
        setAddressCoordinates({ lat: suggestion.data.geo_lat, lon: suggestion.data.geo_lon });
        setAddressWarning(false);
      } else {
        setAddressCoordinates(null);
        setAddressWarning(true);
      }
    } catch (e) {
      setAddressCoordinates(null);
      setAddressWarning(true);
    }
  };

  // Если адрес восстановлен из localStorage без координат — автоматически
  // дёргаем Dadata-suggest на mount чтобы получить координаты и запустить расчёт доставки.
  // Без этого фикса юзер должен был бы кликнуть в поле адреса и blur'нуть.
  useEffect(() => {
    if (formData.delivery_address && formData.delivery_address.length >= 5 && !addressCoordinates) {
      handleAddressBlur(formData.delivery_address);
    }
    // mount-only effect, не зависим от изменений после
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddressFieldChange = (field, value) => {
    updateField('delivery_' + field, value);
    if (field === 'apartment_absent' && value) {
      updateField('delivery_apartment', '');
    }
  };

  // Расчёт стоимости доставки — тот же механизм что в обычном CheckoutModal:
  // требуем И адрес, И координаты. Координаты ставятся либо из подсказок Dadata
  // (handleAddressChange), либо при blur через Dadata-suggest (handleAddressBlur),
  // либо восстанавливаются из localStorage при возврате на форму.
  useEffect(() => {
    if (!formData.delivery_address || formData.delivery_address.length < 3) {
      setDeliveryCalc(null);
      return;
    }
    if (!addressCoordinates) {
      setDeliveryCalc(null);
      return;
    }
    const calc = async () => {
      try {
        const result = await api.calculateDelivery(
          formData.delivery_address, addressCoordinates.lat, addressCoordinates.lon
        );
        setDeliveryCalc(result);
        if (result && !result.error) {
          if (result.zone && !result.requires_negotiation) {
            trackSubscriptionAddressInZone(clientType);
          } else {
            trackSubscriptionAddressOutOfZone(clientType);
          }
        }
      } catch (e) {
        setDeliveryCalc({ error: 'Не удалось рассчитать стоимость доставки' });
      }
    };
    const t = setTimeout(calc, 1000);
    return () => clearTimeout(t);
  }, [addressCoordinates, formData.delivery_address, clientType]);

  // Трекаем старт оформления подписки — один раз на маунте
  useEffect(() => {
    trackSubscriptionStarted(clientType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Телефон =====
  // Форматирование: +7 (XXX) XXX-XX-XX
  const formatPhone = (digits) => {
    if (!digits || digits.length === 0) return '+7';
    let d = digits.replace(/\D/g, '');
    if (d.startsWith('8')) d = '7' + d.slice(1);
    if (d.length > 0 && !d.startsWith('7')) d = '7' + d;
    let r = '+7';
    if (d.length > 1) r += ' (' + d.slice(1, 4);
    if (d.length >= 4) r += ') ';
    if (d.length > 4) r += d.slice(4, 7);
    if (d.length > 7) r += '-' + d.slice(7, 9);
    if (d.length > 9) r += '-' + d.slice(9, 11);
    return r;
  };

  const parsePhoneDigits = (value, prev) => {
    let d = value.replace(/\D/g, '');
    if (d.startsWith('8')) d = '7' + d.slice(1);
    if (d.length > 0 && !d.startsWith('7')) d = '7' + d;
    d = d.slice(0, 11);
    if (prev && d === prev && value.length < formatPhone(prev).length) {
      d = d.slice(0, -1);
    }
    return d;
  };

  const handlePhoneChange = (value) => {
    setPhone(parsePhoneDigits(value, phone));
  };

  const handleRecipientPhoneChange = (value) => {
    setRecipientPhone(parsePhoneDigits(value, recipientPhone));
  };

  // ===== Подсветка поля =====
  const highlightField = (fieldId) => {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      const input = el.querySelector('input, select, .checkout-date-picker');
      if (input) {
        input.style.transition = 'background 0.3s';
        input.style.background = '#FFE0E0';
        if (input.tagName === 'INPUT' || input.tagName === 'SELECT') input.focus();
        setTimeout(() => {
          input.style.background = '';
          setTimeout(() => { input.style.transition = ''; }, 300);
        }, 1000);
      }
    }, 400);
  };

  // ===== Отправка =====
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    // Web-гость без JWT — сначала авторизоваться через SMS или открыть мини-приложение
    if (isWebGuest) {
      setAuthOpen(true);
      return;
    }

    if (phone.length < 11) { highlightField('sub-field-phone'); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { highlightField('sub-field-email'); return; }
    if (clientType === 'individual' && isRecipientOther && !recipientName.trim()) { highlightField('sub-field-recipient-name'); return; }
    // Проверяем нативное значение поля адреса (autofill может не обновить React state)
    const addrInput = document.querySelector('#sub-field-address input');
    if (addrInput && addrInput.value && addrInput.value !== formData.delivery_address) {
      updateField('delivery_address', addrInput.value);
    }
    const effectiveAddress = addrInput?.value || formData.delivery_address;

    if (!effectiveAddress.trim()) { highlightField('sub-field-address'); return; }
    if (!formData.delivery_apartment_absent && !formData.delivery_apartment?.trim()) { highlightField('sub-field-apartment'); return; }
    if (selectedDates.length < delivery_count) { highlightField('sub-field-date'); return; }
    if (!selectedTime) { highlightField('sub-field-time'); return; }
    if (clientType === 'company' && !companyFile && !companySendLater) { setError('Прикрепите карту партнёра или отметьте отправку позже'); return; }

    // Всегда проверяем адрес через Dadata (адрес мог загрузиться из localStorage)
    if (effectiveAddress.trim().length >= 5) {
      setIsSubmitting(true);
      try {
        const result = await api.request(`/address/suggest?query=${encodeURIComponent(effectiveAddress)}&count=1`);
        const suggestion = result.suggestions?.[0];
        if (suggestion?.data?.house && suggestion?.data?.geo_lat && suggestion?.data?.geo_lon && suggestion?.data?.postal_code) {
          setAddressCoordinates({ lat: suggestion.data.geo_lat, lon: suggestion.data.geo_lon });
          setAddressWarning(false);
        } else {
          setAddressWarning(true);
          setIsSubmitting(false);
          highlightField('sub-field-address');
          return;
        }
      } catch (e) {
        setAddressWarning(true);
        setIsSubmitting(false);
        highlightField('sub-field-address');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const payload = {
        catalog_id: catalog_item.id,
        delivery_dates: selectedDates,
        delivery_time: selectedTime,
        delivery_address: effectiveAddress || formData.delivery_address,
        apartment: formData.delivery_apartment_absent ? null : (formData.delivery_apartment || null),
        no_apartment: formData.delivery_apartment_absent || false,
        entrance: formData.delivery_entrance || null,
        floor: formData.delivery_floor || null,
        intercom: formData.delivery_intercom || null,
        is_recipient_other: clientType === 'individual' ? isRecipientOther : false,
        recipient_name: (clientType === 'individual' && isRecipientOther) ? recipientName : null,
        recipient_phone: (clientType === 'individual' && isRecipientOther && recipientPhone.length >= 11) ? formatPhone(recipientPhone) : null,
        payment_type: clientType === 'company' ? 'invoice' : 'card',
        use_bonuses: initialUseBonuses || false,
        odd_quantity: initialOddQuantity || false,
        telegram_id: user.telegram_id || undefined,
        max_id: user.max_id || undefined,
        source_platform: (platform === 'web' ? 'web' : (user.platform || 'telegram')),
        device_platform: getDevicePlatform(),
        touch_token: getTouchToken(),     // откуда пришёл (модуль «Источники заказов»)
        web_client_id: getWebClientId(),
        first_name: name || user.first_name || '',
        last_name: user.last_name || '',
        username: user.username || '',
        phone: phone.length >= 11 ? formatPhone(phone) : null,
        email: email || null,
        consent_pd: consentPd,
        consent_marketing: consentMarketing,
        idempotency_key: idempotencyKey,
      };
      if (addressCoordinates) {
        payload.delivery_lat = addressCoordinates.lat;
        payload.delivery_lon = addressCoordinates.lon;
      }

      const subscription = await api.createSubscription(payload);

      // Загрузка карточки партнёра (если прикреплена)
      if (clientType === 'company' && companyFile) {
        try {
          setFileUploading(true);
          await api.uploadPartnerCard(subscription.id, companyFile);
        } catch (uploadErr) {
          console.error('Partner card upload error:', uploadErr);
          // Не блокируем — подписка уже создана, файл можно отправить позже
        } finally {
          setFileUploading(false);
        }
      }

      // Очищаем сохранённую форму после успешного создания подписки
      try { localStorage.removeItem(SUB_STORAGE_KEY); } catch (e) {}

      if (clientType === 'company') { onSuccess(subscription, 'invoice'); return; }

      // Бонусы покрыли всё — сервер уже отметил подписку оплаченной, платить нечего
      if (subscription.payment_status === 'paid') { onSuccess(subscription, 'bonuses'); return; }

      // Нативный Telegram-инвойс — единый путь оплаты; фолбэк — redirect ниже
      if (await payViaTgInvoice({ id: subscription.id, kind: 'sub', orderNumber: subscription.order_number, total: subscription.total })) {
        onSuccess(subscription, 'card');
        return;
      }

      localStorage.setItem('stems_pending_subscription', JSON.stringify({
        subscription_id: subscription.id, order_number: subscription.order_number, timestamp: Date.now(),
      }));
      onSuccess(subscription, 'card');
      // Backend синхронно создал ЮКасса-платёж и положил confirmation_url
      // в subscription.payment_url. Открываем напрямую.
      if (subscription.payment_url) {
        platformOpenLink(subscription.payment_url);
      } else {
        // Fallback: ЮКасса упала при создании, дёргаем endpoint для retry
        try {
          const _r = await api.getSubscriptionPaymentUrl(subscription.id);
          if (_r?.payment_url) {
            platformOpenLink(_r.payment_url);
          } else {
            setError('Не удалось связаться с платёжным провайдером. Попробуйте позже или свяжитесь с поддержкой сервиса. Подписка сохранена — оплатите её из раздела «Мои заказы».');
          }
        } catch (_e) {
          setError('Не удалось связаться с платёжным провайдером. Попробуйте позже или свяжитесь с поддержкой сервиса. Подписка сохранена — оплатите её из раздела «Мои заказы».');
        }
      }
    } catch (e) {
      const msg = e?.message || '';
      const isNetworkOrUnknown = !msg || msg === 'Request failed' || /failed to fetch|networkerror|load failed/i.test(msg);
      if (isNetworkOrUnknown) {
        setError('Не удалось связаться с платёжным провайдером. Попробуйте позже или свяжитесь с поддержкой сервиса.');
      } else {
        setError(msg);
      }
      setIsSubmitting(false);
    }
  };

  // ===== Рендер компактного календаря с навигацией по месяцам =====
  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const dayNamesShort = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  const dayNamesRu = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

  const [calendarMonth, setCalendarMonth] = useState(() => new Date(minDate.getFullYear(), minDate.getMonth(), 1));

  const calendarMinMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const calendarMaxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const canGoPrev = calendarMonth > calendarMinMonth;
  const canGoNext = calendarMonth < calendarMaxMonth;

  const renderCalendar = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const days = [];
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`e-${i}`} className="calendar-day empty" />);
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      const dateStr = toDateStr(date);
      const isSelected = selectedDates.includes(dateStr);
      const disabled = isDateBlocked(date) || date < today;
      days.push(
        <div
          key={d}
          className={`calendar-day${disabled ? ' disabled' : ''}${isSelected ? ' selected' : ''}`}
          onClick={() => !disabled && toggleDate(date)}
        >{d}</div>
      );
    }

    return (
      <div className="calendar" style={{ border: 'none', boxShadow: 'none', padding: 0 }}>
        <div className="calendar-header">
          <button type="button" className="calendar-nav" onClick={() => canGoPrev && setCalendarMonth(new Date(year, month - 1, 1))} style={{ opacity: canGoPrev ? 1 : 0.3, cursor: canGoPrev ? 'pointer' : 'default' }}>‹</button>
          <span className="calendar-month">{monthNames[month]} {year}</span>
          <button type="button" className="calendar-nav" onClick={() => canGoNext && setCalendarMonth(new Date(year, month + 1, 1))} style={{ opacity: canGoNext ? 1 : 0.3, cursor: canGoNext ? 'pointer' : 'default' }}>›</button>
        </div>
        <div className="calendar-weekdays">
          {dayNamesShort.map(day => <div key={day} className="calendar-weekday">{day}</div>)}
        </div>
        <div className="calendar-days">{days}</div>
      </div>
    );
  };

  const formatSelectedDates = () => {
    if (selectedDates.length === 0) return 'Выберите даты';
    return selectedDates.map(d => {
      const dt = new Date(d + 'T00:00:00');
      return `${dayNamesRu[dt.getDay()]} ${dt.getDate()}.${String(dt.getMonth() + 1).padStart(2, '0')}`;
    }).join(', ');
  };

  // Слоты времени
  const timeSlots = slotsConfig.timeSlots.map(value => ({
    value,
    label: value.replace('-', ' – '),
  }));

  return (
    <div className="checkout-page" onClick={() => dismissVaseTooltip()}>
      <div className="checkout-header">
        <button className="checkout-back" onClick={onBack} type="button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h2>Корзина</h2>
      </div>

      <form className="checkout-form" onSubmit={handleSubmit} noValidate>

        {/* Баннер авторизации для веб-гостей — для подписок auth ОБЯЗАТЕЛЕН */}
        {isWebGuest && (
          <div style={{
            background: '#FFF3E0',
            borderRadius: '10px',
            padding: '14px 16px',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#E65100',
            textAlign: 'center',
          }}>
            <div style={{ marginBottom: '10px', fontWeight: '600' }}>Для оформления подписки требуется авторизация</div>

            {authOpen ? (
              <div style={{ marginBottom: '12px', maxWidth: '300px', margin: '0 auto 12px' }}>
                <SmsAuthInline
                  onSuccess={async () => {
                    const fresh = await fetchWebUser();
                    if (fresh) {
                      setWebUser(fresh);
                      if (fresh.phone) {
                        let d = String(fresh.phone).replace(/\D/g, '');
                        if (d.startsWith('8')) d = '7' + d.substring(1);
                        if (d.length === 10) d = '7' + d;
                        setPhone(d.substring(0, 11));
                      }
                    }
                    setAuthOpen(false);
                  }}
                  onCancel={() => setAuthOpen(false)}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAuthOpen(true)}
                style={{
                  background: 'var(--primary-color)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  color: 'var(--on-primary)',
                  marginBottom: '10px',
                }}
              >
                Авторизация через SMS
              </button>
            )}

            <div style={{ fontSize: '12px', color: '#555', margin: '4px 0 8px' }}>
              или оформите подписку в мини-приложении
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <a
                href={tgBotUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClickTelegram('subscription_checkout')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: '#2AABEE', color: 'white', textDecoration: 'none',
                  borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '600',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.492-1.302.48-.428-.012-1.252-.242-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                Telegram
              </a>
              <a
                href={maxBotUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClickMax('subscription_checkout')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: 'linear-gradient(135deg, #00C8FF 0%, #5B6FFF 50%, #A855F7 100%)',
                  color: 'white', textDecoration: 'none',
                  borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '600',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12c0 1.77.46 3.43 1.27 4.88L2 22l5.12-1.24A9.96 9.96 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 14.5a4.5 4.5 0 110-9 4.5 4.5 0 010 9z" fill="white"/></svg>
                Max
              </a>
            </div>
          </div>
        )}

        {/* 1. Тип клиента */}
        <div className="checkout-field">
          <label>Тип клиента</label>
          <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
            <button type="button" onClick={() => setClientType('individual')}
              style={{
                flex: 1, padding: '10px', border: 'none', cursor: 'pointer',
                background: clientType === 'individual' ? 'var(--primary-color)' : '#fff',
                color: '#333',
                fontWeight: clientType === 'individual' ? 600 : 400, fontSize: 14,
              }}
            >Физическое лицо</button>
            <button type="button" onClick={() => setClientType('company')}
              style={{
                flex: 1, padding: '10px', border: 'none', cursor: 'pointer',
                borderLeft: '1px solid var(--border-color)',
                background: clientType === 'company' ? 'var(--primary-color)' : '#fff',
                color: '#333',
                fontWeight: clientType === 'company' ? 600 : 400, fontSize: 14,
              }}
            >Юридическое лицо</button>
          </div>
        </div>

        {/* 2. Имя */}
        <div className="checkout-field">
          <label>{clientType === 'company' ? 'Имя представителя компании' : 'Имя заказчика'}</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder={clientType === 'company' ? 'Имя представителя' : 'Ваше имя'} />
        </div>

        {/* 3. Телефон */}
        <div className="checkout-field" id="sub-field-phone">
          <label>Телефон *</label>
          <input type="tel" value={formatPhone(phone)} onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder="+7 (999) 888-22-22" maxLength={18} />
        </div>

        {/* 4. Email */}
        <div className="checkout-field" id="sub-field-email">
          <label>E-mail *</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="example@mail.ru" />
        </div>

        {/* 5. Получатель (только физлицо) */}
        {clientType === 'individual' && (
          <>
            <div className="checkout-checkbox">
              <label>
                <input type="checkbox" checked={isRecipientOther} onChange={e => setIsRecipientOther(e.target.checked)} />
                <span>Получатель другой человек</span>
              </label>
            </div>

            {isRecipientOther && (
              <>
                <div className="checkout-field" id="sub-field-recipient-name">
                  <label>Имя получателя *</label>
                  <input type="text" value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="Введите имя получателя" />
                </div>
                <div className="checkout-field">
                  <label>Телефон получателя *</label>
                  <input type="tel" value={formatPhone(recipientPhone)} onChange={e => handleRecipientPhoneChange(e.target.value)} placeholder="+7 (999) 888-22-22" maxLength={18} />
                </div>
              </>
            )}
          </>
        )}

        {/* 6. Адрес доставки */}
        <AddressBlock
          address={formData.delivery_address}
          apartment={formData.delivery_apartment}
          entrance={formData.delivery_entrance}
          floor={formData.delivery_floor}
          intercom={formData.delivery_intercom}
          apartmentAbsent={formData.delivery_apartment_absent}
          addressWarning={addressWarning}
          deliveryLoading={false}
          deliveryCalc={null}
          onAddressChange={(address, data) => handleAddressChange(address, data)}
          onAddressBlur={(val) => handleAddressBlur(val)}
          onFieldChange={(field, value) => handleAddressFieldChange(field, value)}
          fieldIdPrefix="sub-field-"
          placeholder="Улица, дом"
        />

        {deliveryCalc?.requires_negotiation && addressCoordinates && (
          <div style={{
            padding: '12px',
            background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
            border: '1px solid #FFB74D',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#E65100' }}>
              <HeroIcon name="alert" size={16} color="currentColor" inline /> Требуется индивидуальное согласование
            </div>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              {deliveryCalc.zone ? `Расстояние: ${deliveryCalc.zone}` : 'Адрес за пределами зоны доставки'}
              {deliveryCalc.distance && ` (${deliveryCalc.distance} км)`}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Для оформления подписки свяжитесь с{' '}
              <button
                type="button"
                onClick={() => {
                  const platform = getPlatform();
                  if (platform === 'max') {
                    openPlatformLink(maxBotUrl());
                  } else {
                    if (BRAND.supportUrl) openPlatformLink(BRAND.supportUrl);
                  }
                }}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#2196F3', textDecoration: 'underline', cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                поддержкой
              </button>.
            </div>
          </div>
        )}

        {/* 5. Дата доставки — dropdown календарь */}
        <div className="checkout-field" id="sub-field-date">
          <label>Даты доставки * <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>— выберите {delivery_count}</span></label>
          <div
            className="checkout-date-picker"
            ref={datePickerRef}
            onClick={() => setShowCalendar(!showCalendar)}
          >
            <span style={{ color: selectedDates.length > 0 ? '#333' : '#999' }}>{formatSelectedDates()}</span>
            <span className="checkout-date-icon"><FieldIcon name="calendar" /></span>
          </div>
          {showCalendar && (
            <div ref={calendarRef} style={{ marginTop: 8 }}>
              {renderCalendar()}
              <div style={{ marginTop: 4, fontSize: 13, color: '#333', fontWeight: 500 }}>
                Выбрано: {selectedDates.length} / {delivery_count}
              </div>
            </div>
          )}
        </div>

        {/* 6. Время доставки */}
        <div ref={timePickerRef}>
        <TimeSlotPicker
          selectedTime={selectedTime}
          timeSlots={timeSlots}
          hasDate={selectedDates.length > 0}
          isOpen={showTimePicker}
          onToggle={() => setShowTimePicker(!showTimePicker)}
          onSelect={(value) => { setSelectedTime(value); setShowTimePicker(false); }}
          fieldId="sub-field-time"
        />
        </div>
        <div style={{ fontSize: '12px', color: '#999', marginTop: '-8px', marginBottom: '16px' }}>
          После оформления время каждой доставки можно изменить
        </div>

        {/* 7. Карточка партнёра (юрлицо) */}
        {clientType === 'company' && (
          <div style={{ background: '#FFF8E1', padding: 14, borderRadius: 8, fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Карта партнёра</div>

            {/* Прикрепить файл */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  if (f.size > 10 * 1024 * 1024) {
                    setError('Файл слишком большой. Максимум 10 МБ');
                    return;
                  }
                  setCompanyFile(f);
                  setCompanySendLater(false);
                }
                e.target.value = '';
              }}
            />

            {companyFile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff', borderRadius: 6, marginBottom: 8 }}>
                <HeroIcon name="clip" size={16} color="currentColor" inline />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{companyFile.name}</span>
                <button type="button" onClick={() => setCompanyFile(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 16 }}>✕</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: '100%', padding: '10px', background: '#fff', border: '1px dashed #ccc',
                  borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#333', marginBottom: 8,
                }}
              >
                <HeroIcon name="clip" size={16} color="currentColor" inline /> Прикрепить карту партнёра
              </button>
            )}

            <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
              PDF, DOCX, JPG, PNG — до 10 МБ
            </div>

            {/* Или отправить позже */}
            {!companyFile && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={companySendLater} onChange={e => setCompanySendLater(e.target.checked)} style={{ marginTop: 2 }} />
                <span>Вышлю карточку партнера в чат поддержки</span>
              </label>
            )}

            <div style={{ marginTop: 10, fontSize: 12, color: '#888', fontStyle: 'italic' }}>
              Счёт на оплату будет отправлен на электронный адрес и в telegram после оформления заказа
            </div>
          </div>
        )}

        {/* 9. Итого */}
        <div style={{ background: '#f8f8f8', borderRadius: 12, padding: 16, marginTop: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Ваша подписка</div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 14 }}>
            <span>{catalog_item.name} × {delivery_count}</span>
            <span>{subtotal.toLocaleString('ru-RU')} ₽</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 14, color: '#666' }}>
            <span>Сервисный сбор</span>
            <span>+{service_fee.toLocaleString('ru-RU')} ₽</span>
          </div>

          {discount_amount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 14, color: '#E65100' }}>
              <span>Скидка {discount_percent}%</span>
              <span>−{discount_amount.toLocaleString('ru-RU')} ₽</span>
            </div>
          )}

          {(() => {
            // Доставка подписки:
            // - 'free' → всегда "Бесплатно"
            // - 'standard' + есть цена → "<цена> ₽ × N = total ₽"
            // - 'standard' без цены (адрес ещё не введён) → строку не рендерим
            const isStandard = subDeliveryMode === 'standard';
            const deliveryPerDelivery = isStandard ? (deliveryCalc?.price || 0) : 0;
            const deliveryTotal = deliveryPerDelivery * delivery_count;
            if (isStandard && deliveryPerDelivery <= 0) return null;
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 14, color: '#666' }}>
                <span>Доставка</span>
                {!isStandard ? (
                  <span style={{ color: '#2E7D32' }}>Бесплатно</span>
                ) : (
                  <span>{deliveryPerDelivery.toLocaleString('ru-RU')} ₽ × {delivery_count} = {deliveryTotal.toLocaleString('ru-RU')} ₽</span>
                )}
              </div>
            );
          })()}

          {initialBonusDiscount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 14, color: '#2E7D32' }}>
              <span>Со счёта {BRAND.name}</span>
              <span>−{initialBonusDiscount.toLocaleString('ru-RU')} ₽</span>
            </div>
          )}

          {delivery_count >= vaseMinDeliveries && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, fontSize: 14, color: '#666' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Замена вазы
              <span style={{ position: 'relative' }}>
                <span onClick={(e) => { e.stopPropagation(); setShowVaseTooltip(!showVaseTooltip); }} style={{ cursor: 'pointer', display: 'inline-flex', verticalAlign: '-3px', color: 'var(--text-secondary)' }}><FieldIcon name="info" size={15} /></span>
                {showVaseTooltip && (
                  <div style={{
                    position: 'absolute', left: 0, top: 22, width: 220,
                    background: '#333', color: '#fff', padding: 10,
                    borderRadius: 8, fontSize: 12, lineHeight: 1.4,
                    zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}>
                    При первой доставке вы получаете вазу бесплатно. При каждой последующей доставке курьер заменит вазу на новую.
                    <div style={{
                      position: 'absolute', top: -6, left: 8,
                      width: 0, height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderBottom: '6px solid #333',
                    }} />
                  </div>
                )}
              </span>
            </span>
            <span style={{ color: '#2E7D32' }}>Бесплатно</span>
          </div>
          )}

          {selectedDates.length > 0 && (
            <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 8, marginTop: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Даты доставок:</div>
              {selectedDates.map((d, i) => {
                const dt = new Date(d + 'T00:00:00');
                return (
                  <div key={d} style={{ fontSize: 13, color: '#555' }}>
                    {i + 1}. {dayNamesRu[dt.getDay()]} {dt.getDate()}.{String(dt.getMonth() + 1).padStart(2, '0')}
                    {selectedTime ? ` — ${selectedTime}` : ''}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #e0e0e0', fontSize: 18, fontWeight: 600 }}>
            <span>Итого</span>
            <span>{(() => {
              const subDeliveryAdd = subDeliveryMode === 'standard' ? (deliveryCalc?.price || 0) * delivery_count : 0;
              return (total + subDeliveryAdd).toLocaleString('ru-RU');
            })()} ₽</span>
          </div>
        </div>

        {/* Ошибка */}
        {error && (
          <div style={{ padding: 12, background: '#FFEBEE', borderRadius: 8, color: '#C62828', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {/* Согласия 152-ФЗ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', margin: '14px 0 6px' }}>
          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', color: '#333' }}>
            <input
              type="checkbox"
              checked={consentMarketing}
              onChange={(e) => setConsentMarketing(e.target.checked)}
              style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary-color)' }}
            />
            <span>Согласен получать рекламные и информационные сообщения</span>
          </label>
          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '13px', lineHeight: '1.4', cursor: 'pointer', color: '#333' }}>
            <input
              type="checkbox"
              checked={consentPd}
              onChange={(e) => setConsentPd(e.target.checked)}
              style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary-color)' }}
            />
            <span>
              Я принимаю{' '}
              <a href={legal.policyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1A73E8' }}>
                Политику конфиденциальности
              </a>
              {' '}и даю согласие на обработку персональных данных
            </span>
          </label>
        </div>

        {/* 10. Кнопка */}
        <button
          type="button"
          className="checkout-btn"
          disabled={isSubmitting || isWebGuest || !consentPd || !name.trim() || deliveryCalc?.requires_negotiation || addressWarning}
          onClick={handleSubmit}
          style={{ width: '100%', background: (isSubmitting || isWebGuest || !consentPd || !name.trim() || deliveryCalc?.requires_negotiation || addressWarning) ? '#ccc' : 'var(--primary-color)', color: (isWebGuest ? '#888' : 'var(--on-primary)'), marginTop: 8, cursor: (isWebGuest || !consentPd || !name.trim()) ? 'not-allowed' : 'pointer' }}
          title={isWebGuest ? 'Сначала авторизуйтесь через SMS' : (!consentPd ? 'Необходимо принять Политику конфиденциальности' : (!name.trim() ? 'Укажите имя заказчика' : undefined))}
        >
          {/* Как у обычного заказа (CheckoutModal): всегда «Оформить заказ».
              Сумма уже показана в «Итого» выше; при полной оплате бонусами
              она 0 — и «Оплатить — 0 ₽» читалось как ошибка (замечание 10.09). */}
          {isSubmitting ? (fileUploading ? 'Загружаем файл…' : 'Оформляем…') : isWebGuest ? 'Авторизуйтесь для оформления' : !name.trim() ? 'Укажите имя заказчика' : 'Оформить заказ'}
        </button>

        <p style={{
          fontSize: '10px', color: '#666', textAlign: 'center',
          margin: '8px 0 0', lineHeight: '1.4',
        }}>
          Нажимая «Оформить заказ», вы принимаете условия{' '}
          <a
            href={legal.ofertaUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#666', textDecoration: 'underline' }}
          >
            Публичной оферты
          </a>
        </p>
      </form>
    </div>
  );
}

export default SubscriptionCheckout;
